import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const GeneralReportsList = ({ data = [], filters = {} }) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredData = data.filter((item) => {
    // 1. Global Search
    const searchStr = `${item.date || ""} ${item.hear_about_us || ""} ${item.diagnosis || ""}`.toLowerCase();
    const matchesSearch = searchStr.includes(filterText.toLowerCase());

    // 2. Specific Filters
    const matchesSource = !filters.source || item.hear_about_us === filters.source;
    const matchesGender = !filters.gender || item.gender === filters.gender;

    // 3. Date Range
    let matchesDate = true;
    if (filters.startDate && item.date < filters.startDate) matchesDate = false;
    if (filters.endDate && item.date > filters.endDate) matchesDate = false;

    return matchesSearch && matchesSource && matchesGender && matchesDate;
  });

  const totalRows = filteredData.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  const columns = [
    {
      name: "Date",
      selector: (row) => row.date || "N/A",
      sortable: true,
    },
    {
      name: "Source (Hear About Us)",
      selector: (row) => row.hear_about_us || "N/A",
      sortable: true,
      cell: (row) => <span className="text-capitalize">{row.hear_about_us || "N/A"}</span>
    },
    {
      name: "Age",
      selector: (row) => row.age || "N/A",
      sortable: true,
      width: "80px",
    },
    {
      name: "Gender",
      selector: (row) => row.gender || "N/A",
      sortable: true,
      width: "100px",
      cell: (row) => <span className="text-capitalize">{row.gender || "N/A"}</span>
    },
    {
      name: "Education",
      selector: (row) => row.education || "N/A",
      sortable: true,
      cell: (row) => <span className="text-capitalize">{row.education || "N/A"}</span>
    },
    {
      name: "Diagnosis",
      selector: (row) => row.diagnosis || "N/A",
      sortable: true,
      cell: (row) => <span className="text-capitalize">{row.diagnosis || "N/A"}</span>
    },
    {
      name: "Duration of Illness",
      selector: (row) => row.duration_of_illness || "N/A",
      sortable: true,
      cell: (row) => <span className="text-capitalize">{row.duration_of_illness || "N/A"}</span>
    },
  ];

  const getSummaryData = () => {
    const total = filteredData.length;
    const male = filteredData.filter(d => d.gender === "Male").length;
    const female = filteredData.filter(d => d.gender === "Female").length;

    const sourceCounts = filteredData.reduce((acc, d) => {
      acc[d.hear_about_us] = (acc[d.hear_about_us] || 0) + 1;
      return acc;
    }, {});

    const topSource = Object.entries(sourceCounts).sort((a,b) => b[1] - a[1])[0]?.[0] || "N/A";

    return { total, male, female, topSource };
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const summary = getSummaryData();

    // Header
    doc.setFontSize(18);
    doc.text("General Report", 14, 15);

    // Summary Section
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Total Inquiries: ${summary.total}`, 14, 25);
    doc.text(`Gender Breakdown: Male: ${summary.male}, Female: ${summary.female}`, 14, 30);
    doc.text(`Top Source: ${summary.topSource}`, 14, 35);

    // Reset for Table
    doc.setTextColor(0);
    const tableColumn = ["Date", "Source", "Age", "Gender", "Education", "Diagnosis", "Duration"];
    const tableRows = filteredData.map(d => [
      d.date || "N/A",
      d.hear_about_us || "N/A",
      d.age || "N/A",
      d.gender || "N/A",
      d.education || "N/A",
      d.diagnosis || "N/A",
      d.duration_of_illness || "N/A"
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 45,
    });
    doc.save("general_report.pdf");
  };

  const getCSVData = () => {
    const summary = getSummaryData();
    const headers = ["Date", "Source (Hear About Us)", "Age", "Gender", "Education", "Diagnosis", "Duration of Illness"];
    const rows = filteredData.map(d => [
      d.date || "N/A",
      d.hear_about_us || "N/A",
      d.age || "N/A",
      d.gender || "N/A",
      d.education || "N/A",
      d.diagnosis || "N/A",
      d.duration_of_illness || "N/A"
    ]);

    return [
      ["General Report Summary"],
      ["Total Inquiries", summary.total],
      ["Male / Female", `${summary.male} / ${summary.female}`],
      ["Top Source", summary.topSource],
      [], // blank line
      headers,
      ...rows
    ];
  };

  const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
          <select className="form-select form-select-sm" style={{ width: "80px" }} value={rowsPerPage} onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}>
            {[5, 10, 15, 20, 25].map((option) => (<option key={option} value={option}>{option}</option>))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage - 1, rowCount)} disabled={currentPage === 1}>
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>Page {currentPage} of {totalPages}</span>
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage + 1, rowCount)} disabled={currentPage === totalPages}>
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
              <li><CSVLink data={getCSVData()} filename="general_report.csv" className="dropdown-item"><i className="ri-file-excel-line me-1 text-success"></i> CSV</CSVLink></li>
            </ul>
          </div>
          <input type="text" placeholder="Search report..." className="form-control form-control-sm" style={{ width: "200px" }} value={filterText} onChange={(e) => setFilterText(e.target.value)} />
        </div>
        <h6 className="mb-0 text-center text-muted flex-grow-1">Showing {start}-{end} of {totalRows} records</h6>
      </div>

      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={<div className="p-3 text-muted">No records found</div>}
        />
      </div>
    </div>
  );
};

export default GeneralReportsList;
